/**
 * Tier Features Configuration
 *
 * Defines feature comparison between Verified Business (Free) and Professional (Paid) tiers
 * for display in account management modal
 */

export interface TierFeature {
  name: string
  verified: boolean | string  // true/false for checkmark, string for specific text like "8 max"
  professional: boolean | string
  badge?: string  // Optional badge text like "Launch Auto!"
}

export interface TierFeatureSection {
  title: string
  features: TierFeature[]
}

export const TIER_PRICE = {
  verified: 'Free',
  // Note: These are fallback values. Actual prices are fetched dynamically from Stripe in components.
  professional: '$15.00/month',
  professionalYearly: '$150.00/year',
} as const

export const TIER_FEATURES: TierFeatureSection[] = [
  {
    title: 'Profile & Basics',
    features: [
      { name: 'Business profile with custom badge', verified: true, professional: true },
      { name: 'Listed in directory', verified: true, professional: true },
      { name: 'Link to your website and social accounts', verified: true, professional: true },
      { name: 'Highlight Certifications', verified: true, professional: true },
    ],
  },
  {
    title: 'Products/Vendors',
    features: [
      { name: 'Add Products/Vendors to profile', verified: '8 max', professional: 'Unlimited' },
      { name: 'Add branded products to profile (producers)', verified: false, professional: true },
    ],
  },
  {
    title: 'Feed Posts',
    features: [
      {
        name: 'Create posts with products, recipes, text & photos',
        verified: '1 per week',
        professional: 'Unlimited'
      },
      { name: 'AI text-to-post help', verified: false, professional: true },
      { name: 'Auto-share to Facebook/Instagram', verified: false, professional: true },
      { name: 'Pre-schedule feed posts', verified: false, professional: true },
    ],
  },
  {
    title: 'Discovery & Visibility',
    features: [
      { name: 'Searchable by products (producers)', verified: true, professional: true },
      { name: 'Promoted in "Products nearby me" list', verified: false, professional: true },
      { name: 'Top placement on search results', verified: false, professional: true },
      { name: 'Featured in "where to buy" recipe matches', verified: false, professional: true },
    ],
  },
]

/**
 * Get display name for tier
 */
export function getTierDisplayName(membershipType: string | null | undefined): string {
  const normalized = membershipType?.toLowerCase()

  if (normalized === 'verified business' || normalized === 'verified') {
    return 'Verified Business'
  }

  if (normalized === 'enhanced' || normalized === 'professional') {
    return 'Professional'
  }

  return 'Verified Business' // Default
}

/**
 * Check if user is on professional/paid tier
 * Checks both membership_type field AND enhanced flag for backwards compatibility
 */
export function isProfessionalTier(
  membershipType: string | null | undefined,
  enhanced?: boolean | null
): boolean {
  // If enhanced flag is explicitly true, user is professional
  if (enhanced === true) {
    return true
  }

  // Otherwise check membership_type string
  const normalized = membershipType?.toLowerCase()
  return normalized === 'enhanced' || normalized === 'professional'
}

/**
 * Check if user is on free/verified tier
 */
export function isVerifiedTier(
  membershipType: string | null | undefined,
  enhanced?: boolean | null
): boolean {
  return !isProfessionalTier(membershipType, enhanced)
}
